import { throttle } from 'lodash'
import { Map } from 'ms-map-gl'
import { FC, memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useMediaQuery, useTheme } from 'ui-components'
import { useGeojsonData } from '~map-preview/hooks/useGeojsonData'
import { useMapId } from '~map-preview/states/map'
import { useMapNavStore } from '~map-preview/states/mapNav'
import {
  useMapToolbarStore,
  VISUALIZATION_TYPES
} from '~map-preview/states/mapToolbar'
import { useMarkerType } from '~map-preview/states/marker'
import { setViewState, useStyle } from '~map-preview/states/viewState'
import { useFilterSelectionSet } from './handlers/filterSelectionSetHandler'
import { useHandlePanning } from './handlers/useHandlePanning'
import { useInitialViewState } from './handlers/useInitialViewState'
import { usePmtiles } from './handlers/usePmtiles'
import { useSetFitBounds } from './handlers/useSetFitBounds'
import { useSetPitch } from './handlers/useSetPitch'
import { Marker } from './Marker'
import { PMTilesSource } from './Sources'
import { ClusterSource } from './Sources/ClusterSource'
import { GeojsonSource } from './Sources/GeojsonSource'
import { H3Source } from './Sources/H3Source'

type Props = {
  children?: React.ReactNode
}

const MapGl: FC<Props> = memo((props) => {
  const { children } = props
  const { setMapAttributionText, mapAttributionText } = useMapNavStore()

  const theme = useTheme()
  const hideAttribute = useMediaQuery(theme.breakpoints.down(1280))
  const { visualization } = useMapToolbarStore()

  const dispatch = useDispatch()

  const mapId = useMapId()

  const markerType = useMarkerType()

  const geojson = useGeojsonData()
  const style = useStyle()

  usePmtiles()
  useFilterSelectionSet()
  useSetFitBounds()
  useHandlePanning()
  useSetPitch()

  const initialViewState = useInitialViewState()

  const handleMove = throttle((event) => {
    const viewState = event.viewState
    dispatch(setViewState(viewState))
  }, 1000)

  const handleAttributionTextChange = useCallback(
    (text: string) => {
      if (text !== mapAttributionText) {
        setMapAttributionText(text)
      }
    },
    [setMapAttributionText, mapAttributionText]
  )

  const key = Object.keys(visualization)[0]
  const visualizationType = key
    ? visualization[key].type
    : VISUALIZATION_TYPES['marker']

  return (
    <Map
      // ::note::
      // key set to mapId to force rerendering of the map when the mapId changes
      key={mapId}
      onMapInstance={(map) => {
        const attributionElement = map
          .getContainer()
          .querySelector('.maplibregl-ctrl-attrib')

        // Get the text content
        if (attributionElement) {
          handleAttributionTextChange(attributionElement.textContent || '')
        }
      }}
      style={style}
      initialViewState={initialViewState}
      navigationControl={false}
      attributionControl={hideAttribute as any}
      dragRotate
      onMoveEnd={handleMove}
    >
      <GeojsonSource geojson={geojson} />

      {visualizationType === VISUALIZATION_TYPES['hexagon'] && (
        <H3Source geojson={geojson} />
      )}

      <ClusterSource geojson={geojson} />

      {visualizationType === VISUALIZATION_TYPES['aggregate'] && (
        <PMTilesSource geojson={geojson} />
      )}

      <Marker />

      {children}
    </Map>
  )
})

export { MapGl }
